import imageCompression from "browser-image-compression";
import React from "react";
import useResponseFlash from "../../../../../hooks/useResponseFlash";
import Flex from "../../../../structure/Flex";
import Icon from "../../../Icon";

interface FileUploadProps {
  value: any;
  setValue: React.Dispatch<any>;
  name: string;
  width?: string;
  height?: string;
  objectFit?: string;
  placeholder?: string;
  imageExtensions?: string[];
  fileAccept?: string;
  videoExtensions?: string[];
}

const IMAGES_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "svg"];
const VIDEOS_EXTENSIONS = ["mp4", "mpeg", "avi"];

const blobToFile = (blob: Blob, name: string) =>
  new File([blob], name, { type: blob.type });

const getFileExtension = (f: any) => {
  let name =
    f instanceof File || Object.keys(f).indexOf("name") !== -1
      ? f.name
      : `${f}`;
  return name.substr(name.lastIndexOf(".") + 1);
};

const FileUpload: React.FC<FileUploadProps> = ({
  value,
  setValue,
  name,
  width = "w-72",
  height = "h-72",
  objectFit = "",
  fileAccept,
  placeholder,
  imageExtensions = IMAGES_EXTENSIONS,
  videoExtensions = VIDEOS_EXTENSIONS,
}) => {
  const [response, setResponse] = useResponseFlash(3000);

  const handleView = () => {
    window.open(
      value instanceof File ? URL.createObjectURL(value) : `${value}`,
      "_blank"
    );
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const _file = (e.target.files || [""])[0];
    if (_file instanceof File) {
      const extension = getFileExtension(_file);
      const fileInMb = _file.size / 1024 / 1024;
      if (
        extension === "jpeg" ||
        extension === "jpg" ||
        extension === "png" ||
        extension === "svg"
      ) {
        const options = {
          maxSizeMB: 5,
          maxWidthOrHeight: 2500,
          useWebWorker: true,
        };
        try {
          const compressedBlob = await imageCompression(_file, options);
          const compressedFile = blobToFile(compressedBlob, _file.name);
          setValue(compressedFile);
        } catch (error) {
          console.log(error);
        }
      } else if (["mp4"].indexOf(extension) !== -1) {
        if (fileInMb < 16) {
          setValue(_file);
        } else {
          setResponse("Video file should be less than 16 MB");
        }
      } else if (["mp3"].indexOf(extension) !== -1) {
        if (fileInMb < 16) {
          setValue(_file);
        } else {
          setResponse("Audio file should be less than 16 MB");
        }
      } else if (
        ["pdf", "doc", "docx", "xls", "xlsx", "csv"].indexOf(extension) !== -1
      ) {
        if (fileInMb < 100) {
          setValue(_file);
        } else {
          setResponse("Document should be less than 100 MB");
        }
      } else {
        setResponse("File not supported.");
        setValue("");
      }
    }
  };

  return (
    <Flex
      inline
      col
      items="center"
      className={`${width} border rounded-lg overflow-hidden`}
    >
      {value ? (
        <Flex items="center" className={`${width} bg-accent relative group`}>
          {imageExtensions.indexOf(getFileExtension(value)) !== -1 ? (
            <img
              src={
                value instanceof File ? URL.createObjectURL(value) : `${value}`
              }
              alt={name}
              className={`${width} ${height} bg-accent origin-center ${objectFit}`}
            />
          ) : videoExtensions.indexOf(getFileExtension(value)) !== -1 ? (
            <video
              className={`${width} ${height} bg-accent origin-center ${objectFit}`}
              autoPlay
              muted
              loop
            >
              <source
                src={
                  value instanceof File
                    ? URL.createObjectURL(value)
                    : `${value}`
                }
              />
            </video>
          ) : (
            <div className={`${width} ${height} bg-accent`} />
          )}
          <Flex
            items="center"
            justify="center"
            className="opacity-0 bg-accent absolute inset-0 bg-clear-black-25 group-hover:opacity-100"
          >
            <button
              className="text-white mx-2 p-2 focus:outline-none"
              type="button"
              onClick={handleView}
            >
              <Icon className="text-2xl">visibility</Icon>
            </button>
            <button
              className="text-white mx-2 p-2 focus:outline-none"
              type="button"
            >
              <label htmlFor={name} className="cursor-pointer">
                <Icon className="text-2xl">edit</Icon>
              </label>
            </button>
          </Flex>
        </Flex>
      ) : (
        <label
          htmlFor={name}
          className={`flex flex-col items-center justify-center ${width} ${height}  p-3 text-subtitle border bg-white`}
        >
          <Icon className="text-2xl">cloud_upload</Icon>
          {placeholder && <span className="text-sm mt-1">{placeholder}</span>}
          {response && <span className="text-sm mt-1">{response}</span>}
        </label>
      )}
      <input
        type="file"
        hidden
        accept={fileAccept}
        id={name}
        className="cursor-pointer focus:shadow-outline focus:outline-none"
        onChange={handleChange}
      />
    </Flex>
  );
};

export default FileUpload;
